/**
 * CP-060 Wave 5 — mirrors backend buildAdditionalUnloadAllocationPayload.
 */
export type ExistingAllocationRow = {
  id?: number;
  cargoId: number;
  allocatedQuantity: number;
  deliveredQuantity?: number;
  fromStopId?: number | null;
  toStopId?: number | null;
};

export type SplitIntent = {
  allocationId: number;
  cargoId: number;
  pendingQty: number;
  keepQty: number;
  moveQty: number;
  fromStopId?: number;
  originalToStopId?: number;
};

export type AllocationPayloadItem = {
  cargoId: number;
  quantity: number;
  fromStopId?: number;
  toStopId?: number;
};

export function buildAdditionalUnloadAllocationPayload(params: {
  existingAllocations: ExistingAllocationRow[];
  splits: SplitIntent[];
}): AllocationPayloadItem[] {
  const activeSplits = params.splits.filter((row) => row.moveQty > 0);
  if (activeSplits.length === 0) return [];

  const affectedCargoIds = new Set(activeSplits.map((row) => row.cargoId));
  const splitByAllocationId = new Map(activeSplits.map((row) => [row.allocationId, row]));
  const items: AllocationPayloadItem[] = [];

  for (const allocation of params.existingAllocations) {
    const cargoId = Math.trunc(allocation.cargoId);
    if (!affectedCargoIds.has(cargoId)) continue;

    const allocationId = Math.trunc(Number(allocation.id ?? 0));
    const split = splitByAllocationId.get(allocationId);
    const allocated = Number(allocation.allocatedQuantity ?? 0);
    const delivered = Number(allocation.deliveredQuantity ?? 0);
    const pending = Math.max(0, allocated - delivered);

    if (split) {
      const keepQty = Math.max(0, Math.min(split.keepQty, pending));
      const moveQty = Math.max(0, Math.min(split.moveQty, pending - keepQty));
      const keepAllocated = delivered + keepQty;

      if (keepAllocated > 0 && split.originalToStopId != null) {
        items.push({
          cargoId,
          quantity: keepAllocated,
          fromStopId: split.fromStopId,
          toStopId: split.originalToStopId
        });
      }
      if (moveQty > 0) {
        items.push({
          cargoId,
          quantity: moveQty,
          fromStopId: split.fromStopId
        });
      }
      continue;
    }

    if (allocated <= 0) continue;
    items.push({
      cargoId,
      quantity: allocated,
      fromStopId:
        allocation.fromStopId != null ? Math.trunc(Number(allocation.fromStopId)) : undefined,
      toStopId: allocation.toStopId != null ? Math.trunc(Number(allocation.toStopId)) : undefined
    });
  }

  return items;
}
